import { FC, ReactElement, ReactNode } from 'react'

import { clsx } from 'clsx'

import {
  FullScreenEnterIcon,
  FullScreenExitIcon,
  XIcon,
} from '../icons/index.js'
import { useModalContext } from './ModalContext.js'
import { labelledById } from './utils.js'

export interface ModalHeaderProps
  extends Omit<React.ComponentProps<'div'>, 'title'> {
  title?: string | ReactNode | ReactElement
  description?: ReactNode | ReactElement
  withBorder?: boolean
}

export const ModalHeader: FC<ModalHeaderProps> = props => {
  const { id, onClose, zenMode, showZenModeBtn, showCloseBtn, toggleZenMode } =
    useModalContext()

  const {
    title,
    description,
    withBorder = false,
    className,
    children,
    ...rest
  } = props

  return (
    <div
      className={clsx(
        'px-4 py-5 sm:p-6',
        'flex justify-between',
        !withBorder && 'pb-0 sm:pb-0',
        withBorder && 'border-b border-line-subdued',
        'items-start',
        className,
      )}
      {...rest}
    >
      {children || (
        <>
          <div className="text-center sm:mt-0 sm:text-start min-w-0 break-words">
            <h3 id={labelledById(id)} className="text-content">
              {title}
            </h3>
            {description && (
              <div className="mt-1">
                <p className="text-content-subdued">{description}</p>
              </div>
            )}
          </div>
        </>
      )}

      {(showZenModeBtn || showCloseBtn) && (
        <div>
          <h3 className="space-s-2 flex">
            {showZenModeBtn && (
              <button
                tabIndex={0}
                type="button"
                className="hidden sm:block bg-surface rounded-md text-content-subdued hover:text-content-hovered focus:outline-none focus-visible:ring"
                onClick={toggleZenMode}
              >
                <span className="sr-only">Zen Mode</span>
                {!zenMode ? (
                  <FullScreenEnterIcon
                    className="relative"
                    style={{ width: '1em', height: '1em', top: '0.125em' }}
                    aria-hidden="true"
                  />
                ) : (
                  <FullScreenExitIcon
                    className="relative"
                    style={{ width: '1em', height: '1em', top: '0.125em' }}
                    aria-hidden="true"
                  />
                )}
              </button>
            )}
            {showCloseBtn && (
              <button
                tabIndex={0}
                type="button"
                className="bg-transparent rounded-md text-content-subdued hover:text-content-hovered focus:outline-none focus-visible:ring"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <XIcon
                  className="relative"
                  style={{ width: '1em', height: '1em', top: '0.125em' }}
                  aria-hidden="true"
                />
              </button>
            )}
          </h3>
        </div>
      )}
    </div>
  )
}
